import { getCreature } from '../domain/roster';

/**
 * How the shared placeholder art takes a creature's own colours.
 *
 * Both placeholder files - the reel portrait and the walk sheet - are drawn in two
 * flat tones: one body colour and one eye colour, with nothing in between. Giving
 * a creature its own colour is therefore not a tint. A tint multiplies, and
 * multiplying a near-black body leaves it near-black whatever colour is asked for,
 * which is why the shared art cannot simply be tinted per species. It is a swap of
 * those two tones for the creature's own.
 *
 * One module performs that swap for both the portrait and the walk sheet, from the
 * same roster colours, so what a creature looks like in a slot and what it looks
 * like in the arena cannot fall out of step.
 */

/** The two tones one placeholder file is drawn in. */
export interface PlaceholderTones {
  readonly body: number;
  readonly eye: number;
}

/** The colours one creature's placeholder art is redrawn in. */
export interface PlaceholderPalette {
  readonly body: number;
  readonly eye: number;
}

/**
 * The tones in `public/sprites/portraits/garaimon-placeholder.png`.
 *
 * Must match what `scripts/prepare_portrait.py` writes, and
 * `npm run art:review` fails when the file stops agreeing with it. This is the
 * same arrangement as `CREATURE_SPRITE_SHEETS`, which states each sheet's frame
 * size and ground row: a fact about a generated file lives in the code that reads
 * it, next to the check that keeps it true.
 */
export const PORTRAIT_TONES: PlaceholderTones = { body: 0x000100, eye: 0x69f422 };

/** The tones in `public/sprites/garaimon/spritesheet.png`. Must match `scripts/prepare_sprites.py`. */
export const WALK_TONES: PlaceholderTones = { body: 0x010101, eye: 0x69ce36 };

/**
 * The brightest channel a placeholder body may use. This is what makes the colour
 * deep, and it is the number the readability of every creature hangs on.
 *
 * The art is drawn in the creature's colour but darkened, not in the colour itself.
 * At full brightness most of the roster measures between 1:1 and 2:1 against the
 * light well of the reel slot and against the arena's grass, so a green or an olive
 * creature would be all but invisible in both. Scaling all three channels by a
 * single factor keeps the hue and the palette's own saturation - which is what
 * keeps one creature apart from another - and lowers only the brightness, which is
 * what buys the contrast back.
 *
 * At 80 every colour in the roster clears 3:1 against the slot well and 4:1 against
 * the arena; both floors are asserted in `placeholderColours.test.ts`, so a new
 * creature whose colour cannot be read is a failing test rather than a creature
 * nobody can see.
 */
export const BODY_PEAK = 80;

/** The deep version of a colour: same hue and saturation, lower brightness. */
export function deepBodyColour(colour: number): number {
  const red = (colour >> 16) & 0xff;
  const green = (colour >> 8) & 0xff;
  const blue = colour & 0xff;
  const peak = Math.max(red, green, blue);
  // Already at or below the target, so it is left alone. Brightening a creature's
  // colour to reach the target would undo the contrast for the darker half of the
  // palette, which is the half that already reads well.
  if (peak <= BODY_PEAK) return colour;
  const scale = BODY_PEAK / peak;
  return (Math.round(red * scale) << 16) | (Math.round(green * scale) << 8) | Math.round(blue * scale);
}

/**
 * The colours a creature's placeholder art is drawn in.
 *
 * The body takes the creature's own colour, deepened. The eyes take its accent at
 * full strength, so they stay a highlight against the body they sit on - the eyes
 * are a few pixels at portrait size, and darkening them with the body would lose
 * the only other thing that tells two creatures apart.
 */
export function placeholderPalette(speciesId: string): PlaceholderPalette {
  const creature = getCreature(speciesId);
  return { body: deepBodyColour(creature.color), eye: creature.accent };
}

/**
 * Replaces the placeholder's two tones with a creature's palette, in place.
 *
 * Returns how many opaque pixels matched neither tone. That is zero for the art as
 * it stands. Anything else means the file is no longer drawn in the two tones this
 * module was told to swap, so the caller reports it: the alternative is a creature
 * quietly rendered in the wrong colours, which looks like the feature simply not
 * working rather than like a broken asset.
 */
export function recolourPlaceholder(pixels: Uint8ClampedArray, tones: PlaceholderTones, palette: PlaceholderPalette): number {
  const [bodyRed, bodyGreen, bodyBlue] = channels(tones.body);
  const [eyeRed, eyeGreen, eyeBlue] = channels(tones.eye);
  const [nextBodyRed, nextBodyGreen, nextBodyBlue] = channels(palette.body);
  const [nextEyeRed, nextEyeGreen, nextEyeBlue] = channels(palette.eye);
  let unmatched = 0;
  for (let index = 0; index < pixels.length; index += 4) {
    // Transparent pixels carry no colour, so they are not the art's business.
    if (pixels[index + 3] === 0) continue;
    const red = pixels[index]!;
    const green = pixels[index + 1]!;
    const blue = pixels[index + 2]!;
    if (red === bodyRed && green === bodyGreen && blue === bodyBlue) {
      pixels[index] = nextBodyRed;
      pixels[index + 1] = nextBodyGreen;
      pixels[index + 2] = nextBodyBlue;
    } else if (red === eyeRed && green === eyeGreen && blue === eyeBlue) {
      pixels[index] = nextEyeRed;
      pixels[index + 1] = nextEyeGreen;
      pixels[index + 2] = nextEyeBlue;
    } else {
      unmatched += 1;
    }
  }
  return unmatched;
}

function channels(colour: number): [number, number, number] {
  return [(colour >> 16) & 0xff, (colour >> 8) & 0xff, colour & 0xff];
}
